import CalendarDaily from '@/features/calendar/components/CalendarDaily/CalendarDaily';
import CalendarMonthly from '@/features/calendar/components/CalendarMonthly';
import CalendarPeriodSelector from '@/features/calendar/components/CalendarPeriodSelector';
import CalendarPeriodSwitcher from '@/features/calendar/components/CalendarPeriodSwitcher';
import { useCalendarApi } from '@/features/calendar/hooks/useCalendarApi';
import ReservationDetails from '@/features/reservations/components/ReservationDetails';
import { endOfMonth, endOfYear, startOfMonth, startOfYear } from 'date-fns';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DocumentType } from '@/features/calendar/types/calendar-dto';
import { Button } from '@/components/ui/button';
import CalendarAddIcon from '@/components/icons/CalendarAddIcon';
import ReservationManage from '@/features/reservations/components/ReservationManage';
import { cn } from '@/lib/utils';
import CalendarMonthlySingle from '@/features/calendar/components/CalendarMonthlySingle';

type Props = {
	id: string;
	isMobile?: boolean;
};

const ApartmentsDetailsCalendar: FC<Props> = ({ id, isMobile }) => {
	const [viewMode, setViewMode] = useState<'month' | 'year'>(
		isMobile ? 'year' : 'month',
	);
	const [selectedDate, setSelectedDate] = useState(new Date());
	const [searchText, setSearchText] = useState('');
	const [reservationId, setReservationId] = useState('');
	const [reservationApartmentId, setReservationApartmentId] = useState('');
	const [reservationDetailsApartmentId, setReservationDetailsApartmentId] = useState('');
	const [reservationType, setReservationType] = useState<DocumentType>();
	const [reservationCheckIn, setReservationCheckIn] = useState<Date>();
	const [reservationCheckOut, setReservationCheckOut] = useState<Date>();
	const [showReservationDetails, setShowReservationDetails] = useState(false);
	const [showReservationManage, setShowReservationManage] = useState(false);

	const { t } = useTranslation();

	const { calendarEventsListData, isFetchingCalendarEventsListData } =
		useCalendarApi({
			startDate:
				viewMode === 'month'
					? startOfMonth(selectedDate)
					: startOfYear(selectedDate),
			endDate:
				viewMode === 'month'
					? endOfMonth(selectedDate)
					: endOfYear(selectedDate),
			searchText,
			apartmentId: id,
		});

	const handleShowReservationDetails = (
		id: string,
		type: DocumentType,
		apartmentId?: string,
		checkInDate?: Date,
		checkOutDate?: Date,
	) => {
		setReservationId(id);
		setReservationType(type);
		setReservationDetailsApartmentId(apartmentId || '');
		setReservationCheckIn(checkInDate);
		setReservationCheckOut(checkOutDate);
		setShowReservationDetails(true);
	};

	const handleShowEditReservation = (id: string) => {
		setShowReservationDetails(false);
		setReservationApartmentId(id);
		setShowReservationManage(true);
	};

	const handleNewReservation = () => {
		setReservationId('');
		setReservationApartmentId('');
		setShowReservationManage(true);
	};

	return (
		<div className='h-full flex flex-col gap-4 select-none'>
			<div className='flex items-center justify-between gap-2'>
				<h3 className={cn(isMobile && 'text-base')}>
					{t(
						'main.apartments.components.details.list.calendar.title',
					)}
				</h3>
				<CalendarPeriodSelector
					viewMode={viewMode}
					selectedDate={selectedDate}
					setSelectedDate={setSelectedDate}
				/>
				{!isMobile && (
					<>
						<CalendarPeriodSwitcher
							viewMode={viewMode}
							setViewMode={setViewMode}
						/>
						<Button onClick={handleNewReservation}>
							<CalendarAddIcon className='stroke-popover-10' />
							{t('elements.buttons.newReservation')}
						</Button>
					</>
				)}
			</div>
			{viewMode === 'month' && (
				<CalendarDaily
					apartments={calendarEventsListData?.apartments}
					selectedDate={selectedDate}
					searchText={searchText}
					setSearchText={setSearchText}
					showReservationDetails={handleShowReservationDetails}
				/>
			)}
			{viewMode === 'year' && !isMobile && (
				<CalendarMonthly
					apartments={calendarEventsListData?.apartments}
					selectedDate={selectedDate}
					isLoading={isFetchingCalendarEventsListData}
					minWidth='280px'
					showReservationDetails={handleShowReservationDetails}
				/>
			)}

			{viewMode === 'year' && isMobile && (
				<CalendarMonthlySingle
					apartments={calendarEventsListData?.apartments}
					selectedDate={selectedDate}
					setSelectedDate={setSelectedDate}
					isLoading={isFetchingCalendarEventsListData}
					minWidth='280px'
				/>
			)}
			{showReservationDetails && (
				<ReservationDetails
					id={reservationId}
					documentType={reservationType}
					apartmentId={reservationDetailsApartmentId}
					startDate={reservationCheckIn}
					endDate={reservationCheckOut}
					onEdit={handleShowEditReservation}
					onClose={() => setShowReservationDetails(false)}
					isMobile={isMobile}
				/>
			)}
			{showReservationManage && (
				<ReservationManage
					apartmentId={reservationApartmentId || id}
					reservationId={reservationId}
					onClose={() => setShowReservationManage(false)}
				/>
			)}
		</div>
	);
};

export default ApartmentsDetailsCalendar;
